'use client';

import { FC, useMemo, useState } from 'react';
import type { IDataBanner } from '@/services/isomorphic/ContentService';
import { Slide, Slider, type ISliderProps } from '@/react/components/Slider';
import { SliderDirection } from '@/react/components/Slider/ISliderOptions';
import { classes } from '@/next-utils/css-utils/scss-utils';
import { Button } from '@/react/components/core-ui/Button';
import { Icon, IconSizeProp, IconTypes } from '@/react/components/core-ui/Icon';
import { InternalBanner } from './internal/InternalBanner';
import { ArrowVariant } from '../../registry/data/parts/sliderSettings';

import S from './styles.base.module.scss';

export interface IMarketingBannerProps {
  /**
   * The banners that can be rendered as an individual banner or a slider of banners.
   * If no banners are provided no component renders but it would make sense to render an
   * alternative outside this component.
   */
  banners: Array<IDataBanner>;

  /** Slider delay in seconds. */
  sliderDelay: number;
}

/**
 * A marketing banner that is supplied with data from builder, it can render as
 * a single banner or a slider of banners.
 */
export const MarketingBanner: FC<IMarketingBannerProps> = ({
  banners,
  sliderDelay
}) => {
  const [isPaused, setIsPaused] = useState(false);
  const slides = useMemo(() => {
    return banners?.map((banner) => (
      <InternalBanner key={banner.text} banner={banner} />
    ));
  }, [banners]);

  const sliderConfig = {
    height: '100%',
    autoplay: { delay: sliderDelay * 1000 },
    sliderClassName: S.sliderWrapper,
    centeredSlides: true,
    pagination: {
      enabled: true,
      position: 'bottom',
      inset: false,
      direction: SliderDirection.Horizontal,
      wrapperClassName: S.thumbWrapper,
      buttonClassName: S.thumbButton,
      inactiveClassName: S.inactive,
      activeClassName: S.active
    },
    loop: true,
    navigation: {
      enabled: false,
      inset: false,
      arrowVariant: ArrowVariant.AngleLight,
      buttonClassName: S.navigation
    }
  } as ISliderProps;

  if (banners.length === 0) {
    return null;
  }

  const isSlider = slides.length > 1;

  return (
    <div className={classes(S.container, isSlider && S.sliderContainer)}>
      {slides.length > 1 ? (
        <>
          <Slider {...sliderConfig} isPaused={isPaused}>
            {slides.map((slide, i) => {
              return <Slide key={banners[i].text}>{slide}</Slide>;
            })}
          </Slider>
          <div className={S.buttonContainer}>
            <Button
              onClick={() => {
                setIsPaused(!isPaused);
              }}
              variant="text"
            >
              <Icon
                icon={isPaused ? IconTypes.Play : IconTypes.Pause}
                size={IconSizeProp.SizeXS}
              />
            </Button>
          </div>
        </>
      ) : (
        <>{slides[0]}</>
      )}
    </div>
  );
};

export default MarketingBanner;
